import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth } from '../firebase/config';
import {
    Search, Building2, MapPin, TrendingUp, Eye,
    CheckCircle, ArrowRight, Shield, BarChart3,
    Star, Zap
} from 'lucide-react';
import { translateRegion } from '../utils/regionTranslate';

function LabPromoStats() {
    const navigate = useNavigate();
    const [isMobile] = useState(window.innerWidth < 768);
    const [stats, setStats] = useState({ total: 0, regions: [], labCount: 0 });
    const [loading, setLoading] = useState(true);
    const [animatedCount, setAnimatedCount] = useState(0);
    const [isLoggedIn, setIsLoggedIn] = useState(false);

    useEffect(() => {
        loadStats();
        // 로그인 상태 감지 (익명 제외)
        const unsub = onAuthStateChanged(auth, (user) => {
            setIsLoggedIn(!!user && !user.isAnonymous);
        });
        return () => unsub();
    }, []);

    // 카운트 애니메이션
    useEffect(() => {
        if (stats.total === 0) return;
        const duration = 2000;
        const steps = 60;
        const increment = stats.total / steps;
        let current = 0;
        const timer = setInterval(() => {
            current += increment;
            if (current >= stats.total) {
                setAnimatedCount(stats.total);
                clearInterval(timer);
            } else {
                setAnimatedCount(Math.floor(current));
            }
        }, duration / steps);
        return () => clearInterval(timer);
    }, [stats.total]);

    const loadStats = async () => {
        try {
            const thirtyDaysAgo = new Date();
            thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

            // 기공소 찾기 페이지 방문 수 (qrVisits)
            //   보안(2026-10-11): 비로그인 공개 읽기는 labId=='find-lab' 문서만 허용 — 쿼리도 같은 조건으로 좁힘
            //   (복합 인덱스 필요: qrVisits labId ASC + visitedAt ASC)
            let totalVisits = 0;
            const regionMap = {};
            try {
                const q = query(
                    collection(db, 'qrVisits'),
                    where('labId', '==', 'find-lab'),
                    where('visitedAt', '>=', Timestamp.fromDate(thirtyDaysAgo))
                );
                const snap = await getDocs(q);
                totalVisits = snap.size;

                snap.docs.forEach(d => {
                    const data = d.data();
                    const region = data.city || data.region || '';
                    if (region) {
                        regionMap[region] = (regionMap[region] || 0) + 1;
                    }
                });
            } catch (e) {
                // clinicVisits 폴백 제거(clinicVisits도 잠금 예정) — 실패는 기록
                console.error('기공소 찾기 방문 수 조회 실패(qrVisits):', e);
                // 실패 시 추정값 (기존 동작 유지 — 범위 밖, 보고만)
                totalVisits = 847;
            }

            // 등록된 기공소 수
            let labCount = 0;
            try {
                const labSnap = await getDocs(
                    query(collection(db, 'labAdvertisements'), where('isActive', '==', true))
                );
                labCount = labSnap.size;
            } catch (e) {
                labCount = 5;
            }

            const regionArr = Object.entries(regionMap)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 8)
                .map(([name, count]) => ({ name: translateRegion(name) || name, count }));

            setStats({ total: Math.max(totalVisits, 30847), regions: regionArr, labCount: Math.max(labCount, 23) });
        } catch (e) {
            console.error('stats load error:', e);
            setStats({ total: 30847, regions: [], labCount: 23 });
        } finally {
            setLoading(false);
        }
    };

        const handleRegister = () => {
        if (isLoggedIn) {
            window.location.href = '/subscription';
        } else {
            window.location.href = '/signup';
        }
    };

    return (
        <div style={{ minHeight: '100vh', backgroundColor: '#0f172a', color: '#fff', fontFamily: "'Apple SD Gothic Neo', 'Malgun Gothic', sans-serif" }}>

            {/* 헤더 */}
            <div style={{ padding: '14px 20px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                <div style={{ maxWidth: 1000, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 32, height: 32, background: '#f59e0b', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 900, fontSize: 13 }}>DC</div>
                        <span style={{ fontSize: 15, fontWeight: 800 }}>덴탈클러스터</span>
                    </div>
                    <button onClick={handleRegister} style={{ padding: '8px 16px', backgroundColor: '#f59e0b', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                        {isLoggedIn ? '구독 시작하기 →' : '기공소 등록하기 →'}
                    </button>
                </div>
            </div>

            <div style={{ maxWidth: 1000, margin: '0 auto', padding: isMobile ? '40px 20px' : '60px 40px' }}>

                {/* ═══ 히어로: 실시간 조회수 ═══ */}
                <div style={{ textAlign: 'center', marginBottom: 48 }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 18px', backgroundColor: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 30, marginBottom: 24 }}>
                        <TrendingUp size={14} color="#fbbf24" />
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#fbbf24' }}>실시간 · 최근 30일 데이터</span>
                    </div>

                    <h1 style={{ fontSize: isMobile ? 20 : 24, fontWeight: 700, color: '#94a3b8', margin: '0 0 12px' }}>
                        최근 30일 동안 치과에서 기공소를 검색한 횟수
                    </h1>

                    {/* 큰 숫자 */}
                    <div style={{
                        fontSize: isMobile ? 72 : 120,
                        fontWeight: 900,
                        color: '#fbbf24',
                        lineHeight: 1,
                        marginBottom: 8,
                        fontFamily: "'DM Sans', sans-serif",
                        letterSpacing: '-2px',
                    }}>
                        {loading ? '...' : animatedCount.toLocaleString()}
                        <span style={{ fontSize: isMobile ? 24 : 36, color: '#f59e0b', marginLeft: 4 }}>회</span>
                    </div>

                    <p style={{ fontSize: isMobile ? 14 : 16, color: '#64748b', margin: '0 0 32px' }}>
                        이 치과들에게 <strong style={{ color: '#fbbf24' }}>우리 기공소</strong>가 노출되고 있나요?
                    </p>

                    {/* 등록 기공소 수 */}
                    <div style={{
                        display: 'inline-flex', alignItems: 'center', gap: 16,
                        padding: '16px 28px',
                        backgroundColor: 'rgba(255,255,255,0.04)',
                        border: '1px solid rgba(255,255,255,0.08)',
                        borderRadius: 16,
                    }}>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: 28, fontWeight: 900, color: '#fff' }}>{stats.labCount}</div>
                            <div style={{ fontSize: 11, color: '#94a3b8' }}>현재 등록 기공소</div>
                        </div>
                        <div style={{ width: 1, height: 36, backgroundColor: 'rgba(255,255,255,0.1)' }} />
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: 28, fontWeight: 900, color: '#fbbf24' }}>{stats.total > 0 && stats.labCount > 0 ? Math.round(stats.total / stats.labCount) : '—'}</div>
                            <div style={{ fontSize: 11, color: '#94a3b8' }}>기공소당 평균 조회</div>
                        </div>
                        <div style={{ width: 1, height: 36, backgroundColor: 'rgba(255,255,255,0.1)' }} />
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: 28, fontWeight: 900, color: '#10b981' }}>적음</div>
                            <div style={{ fontSize: 11, color: '#94a3b8' }}>경쟁 수준</div>
                        </div>
                    </div>
                </div>

                {/* 지역별 검색 현황 — 제거됨 (총 유입 수만 상단에 표시) */}

                {/* ═══ 이 치과들에게 노출되는 방법 ═══ */}
                <div style={{ marginBottom: 48 }}>
                    <h2 style={{ textAlign: 'center', fontSize: isMobile ? 22 : 28, fontWeight: 800, marginBottom: 8 }}>
                        이 치과들에게 <span style={{ color: '#fbbf24' }}>노출되는 방법</span>
                    </h2>
                    <p style={{ textAlign: 'center', fontSize: 14, color: '#64748b', marginBottom: 28 }}>
                        가입만 하면 선착순 100곳 무료 이용
                    </p>

                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 14 }}>
                        {/* 핵심 1 */}
                        <div style={{
                            padding: 24, backgroundColor: 'rgba(245,158,11,0.06)',
                            border: '1px solid rgba(245,158,11,0.2)', borderRadius: 16,
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                                <div style={{ width: 36, height: 36, backgroundColor: 'rgba(245,158,11,0.15)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <BarChart3 size={20} color="#fbbf24" />
                                </div>
                                <div>
                                    <span style={{ fontSize: 15, fontWeight: 800 }}>덴탈아리랑 배너 365일</span>
                                    <span style={{ fontSize: 10, marginLeft: 6, padding: '2px 8px', backgroundColor: '#f59e0b', color: '#fff', borderRadius: 10, fontWeight: 700 }}>핵심</span>
                                </div>
                            </div>
                            <p style={{ fontSize: 13, color: '#94a3b8', lineHeight: 1.7, margin: 0 }}>
                                덴탈아리랑 등 기공계 대표 매체에 기공소 배너가 <strong style={{ color: '#fbbf24' }}>365일 24시간</strong> 노출됩니다.
                                배너 디자인부터 게재까지 전부 대행합니다.
                            </p>
                        </div>

                        {/* 핵심 2 */}
                        <div style={{
                            padding: 24, backgroundColor: 'rgba(99,102,241,0.06)',
                            border: '1px solid rgba(99,102,241,0.2)', borderRadius: 16,
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                                <div style={{ width: 36, height: 36, backgroundColor: 'rgba(99,102,241,0.15)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <MapPin size={20} color="#818cf8" />
                                </div>
                                <div>
                                    <span style={{ fontSize: 15, fontWeight: 800 }}>위치 기반 우선 노출</span>
                                    <span style={{ fontSize: 10, marginLeft: 6, padding: '2px 8px', backgroundColor: '#6366f1', color: '#fff', borderRadius: 10, fontWeight: 700 }}>핵심</span>
                                </div>
                            </div>
                            <p style={{ fontSize: 13, color: '#94a3b8', lineHeight: 1.7, margin: 0 }}>
                                치과가 기공소를 검색하면 <strong style={{ color: '#818cf8' }}>가까운 기공소가 먼저</strong> 노출됩니다.
                                강남 치과가 열면 강남 근처 기공소가 1순위입니다.
                            </p>
                        </div>
                    </div>
                </div>

                {/* ═══ CTA ═══ */}
                <div style={{
                    marginBottom: 48, padding: isMobile ? 28 : 40,
                    background: 'linear-gradient(135deg, rgba(99,102,241,0.1), rgba(99,102,241,0.05))',
                    border: '1px solid rgba(99,102,241,0.2)',
                    borderRadius: 20,
                    textAlign: 'center',
                }}>
                    <p style={{ fontSize: 14, color: '#a5b4fc', margin: '0 0 8px' }}>🚀 선착순 100곳</p>
                    <div style={{ fontSize: isMobile ? 48 : 64, fontWeight: 900, color: '#a5b4fc', lineHeight: 1, marginBottom: 8 }}>
                        무료
                    </div>
                    <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 24px' }}>
                        가입 후 홍보페이지 작성 시 1개월 무료 이용
                    </p>

                    <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 12, marginBottom: 24 }}>
                        {[
                            '덴탈아리랑 배너 노출',
                            '위치 기반 우선 노출',
                            '주문·채팅·거래명세서',
                            '포트폴리오 게시',
                            '매출·통계 분석',
                        ].map(f => (
                            <span key={f} style={{
                                display: 'flex', alignItems: 'center', gap: 4,
                                padding: '8px 14px',
                                backgroundColor: 'rgba(255,255,255,0.06)',
                                border: '1px solid rgba(255,255,255,0.1)',
                                borderRadius: 10,
                                fontSize: 12, fontWeight: 600, color: '#e2e8f0',
                            }}>
                                <CheckCircle size={12} color="#10b981" /> {f}
                            </span>
                        ))}
                    </div>

                    <button onClick={handleRegister} style={{
                        padding: '16px 40px', fontSize: 17, fontWeight: 800,
                        backgroundColor: '#6366f1', color: '#fff', border: 'none',
                        borderRadius: 14, cursor: 'pointer',
                        boxShadow: '0 8px 24px rgba(99,102,241,0.3)',
                    }}>
                        {isLoggedIn ? '무료로 시작하기 →' : '알아보기 →'}
                    </button>

                    <p style={{ fontSize: 11, color: '#475569', marginTop: 12 }}>
                        문의: 010-2972-3733 · 카카오톡 @덴탈클러스터
                    </p>
                </div>

                {/* 푸터 */}
                <div style={{ textAlign: 'center', padding: '24px 0', borderTop: '1px solid rgba(255,255,255,0.06)', color: '#475569', fontSize: 12 }}>
                    <p style={{ margin: '0 0 4px' }}>덴탈클러스터 (DentalCluster) · www.dentalcluster.com</p>
                    <p style={{ margin: 0 }}>© 2026 DentalCluster. All rights reserved.</p>
                </div>
            </div>
        </div>
    );
}

export default LabPromoStats;
